import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';

export interface User {
  id: string;
  email: string;
  name: string;
  permissions: string[];
  mustChangePassword?: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_STORAGE_KEY = 'aipiracy_session_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    try {
      return localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const loginMutation = useMutation(api.auth.login);
  const logoutMutation = useMutation(api.auth.logout);

  // Validate session
  const sessionData = useQuery(
    api.auth.validateSession,
    token ? { token } : "skip"
  );

  const user = sessionData?.user || null;
  const isLoading = token !== null && sessionData === undefined;

  // If session is invalid, clear token
  useEffect(() => {
    if (token && sessionData === null) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      setToken(null);
    }
  }, [token, sessionData]);

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      const result = await loginMutation({ email, password, rememberMe });
      localStorage.setItem(TOKEN_STORAGE_KEY, result.token);
      setToken(result.token);
    } catch (error: any) {
      throw new Error(error.message || 'Login failed');
    }
  };

  const logout = async () => {
    if (token) {
      try {
        await logoutMutation({ token });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setToken(null);
  };

  const hasPermission = (permission: string): boolean => {
    return user?.permissions.includes(permission) || false;
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Permission constants (matches backend)
export const PERMISSIONS = {
  VIEW: 'view',
  SCAN: 'scan',
  ANALYTICS: 'analytics',
  AUTOMATION: 'automation',
  ADMIN: 'admin',
} as const;
